import React, { useEffect, useState } from 'react';

// Streak milestone thresholds and their messages
const STREAK_MILESTONES = [
  { days: 7, title: '1 Week Streak!', emoji: '🌟', message: 'You\'ve built a solid foundation! Keep the momentum going!' },
  { days: 14, title: '2 Week Streak!', emoji: '⚡', message: 'Two weeks of excellence! You\'re developing powerful habits!' },
  { days: 30, title: '30 Day Streak!', emoji: '🏆', message: 'A full month of dedication! You\'re unstoppable now!' },
  { days: 60, title: '60 Day Streak!', emoji: '💎', message: 'Two months of consistency! You\'re becoming a habit master!' },
  { days: 90, title: '90 Day Streak!', emoji: '👑', message: '90 days of excellence! You\'ve truly transformed your business!' },
];

export default function MilestoneCelebration({ streak, onAcknowledge }) {
  const [showModal, setShowModal] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [acknowledgedMilestone, setAcknowledgedMilestone] = useState(null);

  useEffect(() => {
    if (!streak || !streak.current_streak) return;

    // Find the highest milestone reached
    const reachedMilestone = [...STREAK_MILESTONES]
      .reverse()
      .find(m => streak.current_streak >= m.days);

    if (!reachedMilestone) return;

    // Check if this milestone was just acknowledged in this session
    if (acknowledgedMilestone === reachedMilestone.days) return;

    // Check if we've already shown this milestone (using sessionStorage)
    const lastShownMilestone = sessionStorage.getItem('lastShownMilestone');
    if (lastShownMilestone && parseInt(lastShownMilestone, 10) >= reachedMilestone.days) return;

    // Show the milestone celebration
    setCurrentMilestone(reachedMilestone);
    setShowModal(true);
    sessionStorage.setItem('lastShownMilestone', reachedMilestone.days.toString());
  }, [streak?.current_streak, acknowledgedMilestone]);

  const handleClose = () => {
    setShowModal(false);
    if (currentMilestone) {
      setAcknowledgedMilestone(currentMilestone.days);
    }
    if (onAcknowledge) {
      onAcknowledge();
    }
  };

  if (!showModal || !currentMilestone) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
      data-testid="milestone-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform animate-bounce-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="milestone-modal"
      >
        {/* Emoji */}
        <div
          className="text-7xl mb-4 animate-pulse"
          data-testid="milestone-emoji"
        >
          {currentMilestone.emoji}
        </div>

        {/* Title */}
        <h2
          className="text-2xl font-bold text-primary mb-2"
          data-testid="milestone-title"
        >
          {currentMilestone.title}
        </h2>

        {/* Streak count */}
        <div
          className="text-4xl font-bold text-orange-500 mb-4"
          data-testid="milestone-streak-count"
        >
          🔥 {streak.current_streak} Days
        </div>

        {/* Contextual message */}
        <p
          className="text-gray-600 mb-6 text-lg"
          data-testid="milestone-message"
        >
          {currentMilestone.message}
        </p>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="bg-primary text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors"
          data-testid="milestone-close-button"
        >
          Keep Going! 🚀
        </button>
      </div>
    </div>
  );
}
